import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { getERPClient } from "@/lib/erp/client";
import { useDebounce } from "@/lib/hooks/useDebounce";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

type AutocompleteFieldSearchProps = {
  doctype: string; // The DocType (e.g., "Item")
  fieldname: string; // The field to search (e.g., "item_name")
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  operator?: string; // The filter operator (e.g., "like", "equals")
};

/**
 * Autocomplete search for Data/Text fields
 * Searches actual data from the server and shows suggestions
 * Similar to ERPNext's filter autocomplete
 */
export function AutocompleteFieldSearch({
  doctype,
  fieldname,
  value,
  onChange,
  placeholder = "Type to search...",
  disabled = false,
  operator: _operator = "like",
}: AutocompleteFieldSearchProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || "");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  // Request deduplication cache (TTL: 60 seconds, max 50 entries)
  const requestCache = useRef(new Map<string, { data: string[]; timestamp: number }>());
  const CACHE_TTL = 60000; // 60 seconds
  const MAX_CACHE_SIZE = 50;

  // Query cancellation system
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestVersionRef = useRef(0);

  // Sync searchTerm with value prop changes
  useEffect(() => {
    setSearchTerm(value || "");
  }, [value]);

  // Debounce search term to avoid excessive API calls (200-300ms as recommended)
  const debouncedSearchTerm = useDebounce(searchTerm, 250);

  // Auto-abort on popover close
  useEffect(() => {
    if (!open && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Fetch distinct values from server - show initial results when open, or search when typing
  const { data: suggestions = [], isLoading } = useQuery<string[]>({
    queryKey: ["field-autocomplete", doctype, fieldname, debouncedSearchTerm || "initial"],
    queryFn: async ({ signal }) => {
      const client = getERPClient();
      
      // Increment version counter for this request
      const currentVersion = ++requestVersionRef.current;
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      
      // Combine signals (React Query + our abort controller)
      const combinedSignal = signal || abortController.signal;
      
      // Check if request was already aborted
      if (combinedSignal.aborted) {
        throw new Error("Request aborted");
      }

      try {
        const cacheKey = `${doctype}:${fieldname}:${debouncedSearchTerm.trim() || "initial"}`;
        const now = Date.now();

        // Check cache first
        const cached = requestCache.current.get(cacheKey);
        if (cached && (now - cached.timestamp) < CACHE_TTL) {
          // Check version before returning cached data
          if (currentVersion === requestVersionRef.current) {
            return cached.data;
          }
          // Stale request - abort
          throw new Error("Request superseded");
        }

        let results: any[];
        const isSearch = debouncedSearchTerm.trim().length >= 2;

        if (isSearch) {
          // Build filter to search the field
          const filters: any[] = [
            [doctype, fieldname, "like", `%${debouncedSearchTerm.trim()}%`],
          ];

          // Get distinct values from the field using the list method
          results = await client.list({
            doctype,
            filters,
            fields: [fieldname],
            limit_page_length: 20,
            order_by: `${fieldname} asc`,
          });
        } else {
          // For initial results (no search term), get first items from the field
          results = await client.list({
            doctype,
            fields: [fieldname],
            limit_page_length: 20,
            order_by: `${fieldname} asc`,
          });
        }

        // Check version before processing
        if (currentVersion !== requestVersionRef.current) {
          throw new Error("Request superseded");
        }

        // Extract unique values from results
        const uniqueValues = new Set<string>();
        results.forEach((item: any) => {
          const fieldValue = item[fieldname];
          if (fieldValue && typeof fieldValue === "string" && fieldValue.trim()) {
            uniqueValues.add(fieldValue);
          }
        });

        // Sort and return as array
        const data = Array.from(uniqueValues).sort().slice(0, isSearch ? 20 : 15);

        // Store in cache
        requestCache.current.set(cacheKey, { data, timestamp: now });
        
        // Evict oldest entries if cache is full
        if (requestCache.current.size > MAX_CACHE_SIZE) {
          const oldestKey = Array.from(requestCache.current.entries())
            .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0];
          requestCache.current.delete(oldestKey);
        }

        return data;
      } catch (err: any) {
        // Don't log aborted requests as errors
        if (err?.name === "AbortError" || err?.message === "Request aborted" || err?.message === "Request superseded") {
          throw err;
        }
        // For other errors, return empty array
        return [];
      }
    },
    enabled: open, // Fetch when popover is open (whether typing or not)
    staleTime: 0, // Custom cache handles deduplication
    gcTime: 60000, // Keep in cache for 1 minute
  });

  // Virtual scrolling (only for >20 results)
  const shouldUseVirtualScroll = suggestions.length > 20;
  const virtualizer = useVirtualizer({
    count: suggestions.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 36, // CommandItem height ~36px
    overscan: 5,
    enabled: shouldUseVirtualScroll && open,
  });

  // Sync selectedIndex with virtual scroll
  useEffect(() => {
    if (shouldUseVirtualScroll && selectedIndex !== -1 && selectedIndex < suggestions.length) {
      virtualizer.scrollToIndex(selectedIndex, {
        align: "auto",
        behavior: "smooth",
      });
    }
  }, [selectedIndex, shouldUseVirtualScroll, virtualizer, suggestions.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === "Enter" && selectedIndex !== -1 && suggestions[selectedIndex]) {
        e.preventDefault();
        handleSelect(suggestions[selectedIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, suggestions, selectedIndex]);

  const handleSelect = (selectedValue: string) => {
    setSearchTerm(selectedValue);
    onChange(selectedValue);
    setOpen(false);
    setSelectedIndex(-1); // Reset selection
  };

  return (
    <div className="relative w-[200px]">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchTerm(newValue);
                setSelectedIndex(-1); // Reset selection on type
                onChange(newValue || null);
                // Keep popover open when typing or when cleared (to show initial results)
                setOpen(true);
              }}
              onFocus={() => {
                // Always open popover on focus to show initial results
                setOpen(true);
              }}
              placeholder={placeholder}
              disabled={disabled}
              className="w-full"
            />
          </div>
        </PopoverTrigger>
        {open && (
          <PopoverContent className="w-[200px] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
            <Command shouldFilter={false}>
              <CommandList
                ref={listRef}
                className={shouldUseVirtualScroll ? "max-h-[300px] overflow-auto" : ""}
              >
                {isLoading && (
                  <CommandEmpty>Loading...</CommandEmpty>
                )}
                {!isLoading && suggestions.length === 0 && searchTerm.length >= 2 && (
                  <CommandEmpty>No results found</CommandEmpty>
                )}
                {!isLoading && suggestions.length === 0 && searchTerm.length < 2 && (
                  <CommandEmpty>No items available</CommandEmpty>
                )}
                {!isLoading && suggestions.length > 0 && (
                  <CommandGroup>
                    {shouldUseVirtualScroll ? (
                      // Virtual scrolling for >20 results
                      <div
                        style={{
                          height: `${virtualizer.getTotalSize()}px`,
                          width: "100%",
                          position: "relative",
                        }}
                      >
                        {virtualizer.getVirtualItems().map((virtualItem) => {
                          const suggestion = suggestions[virtualItem.index];
                          const isSelected = selectedIndex === virtualItem.index;
                          return (
                            <CommandItem
                              key={`${suggestion}-${virtualItem.index}`}
                              value={suggestion}
                              onSelect={() => handleSelect(suggestion)}
                              className={cn(
                                isSelected && "bg-accent"
                              )}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: `${virtualItem.size}px`,
                                transform: `translateY(${virtualItem.start}px)`,
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  value === suggestion ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {suggestion}
                            </CommandItem>
                          );
                        })}
                      </div>
                    ) : (
                      // Regular rendering for ≤20 results
                      suggestions.map((suggestion, index) => {
                        const isSelected = selectedIndex === index;
                        return (
                          <CommandItem
                            key={suggestion}
                            value={suggestion}
                            onSelect={() => handleSelect(suggestion)}
                            className={cn(
                              isSelected && "bg-accent"
                            )}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                value === suggestion ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {suggestion}
                          </CommandItem>
                        );
                      })
                    )}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}

